import { Button } from "client/designSystem/Button"
import { TextInput } from "client/designSystem/TextInput"
import {
  GiveawayFieldsFragment,
  Giveaways_GiveawayRequiredField as GiveawayRequiredField,
} from "client/graphql/giveaways"
import { useCallback, useMemo, useState } from "react"
import { FormattedMessage } from "react-intl"

import type { EntryFormData } from "./types"

import styles from "./EntryForm.module.css"

type EntryFormProps = {
  openGiveaway: GiveawayFieldsFragment | null
  onSubmit: (formData: EntryFormData) => void
  isLoading: boolean
}

/**
 * The entry form for a giveaway.
 */
export default function EntryForm({
  openGiveaway,
  onSubmit,
  isLoading,
}: EntryFormProps) {
  /**
   * State
   */
  const [formState, setFormState] = useState<EntryFormData>({
    email: "",
    firstName: "",
    lastName: "",
  })

  /**
   * Derived state
   */
  const requiredFields = useMemo(() => {
    return {
      firstName:
        openGiveaway?.requiredFields?.includes(
          GiveawayRequiredField.FirstName,
        ) ?? false,
      lastName:
        openGiveaway?.requiredFields?.includes(
          GiveawayRequiredField.LastName,
        ) ?? false,
      email:
        openGiveaway?.requiredFields?.includes(
          GiveawayRequiredField.Email,
        ) ?? false,
    }
  }, [openGiveaway?.requiredFields])

  /**
   * Handlers
   */
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      setFormState((prev) => ({ ...prev, [name]: value }))
    },
    [],
  )

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      // Omit fields which are not required.
      const parsedFormData = Object.entries(requiredFields)
        .filter(([_, isRequired]) => isRequired)
        .reduce<EntryFormData>(
          (result, [field]) => ({ ...result, [field]: formState[field] }),
          {},
        )

      onSubmit(parsedFormData)
    },
    [onSubmit, formState, requiredFields],
  )

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      {(requiredFields.firstName || requiredFields.lastName) && (
        <div className={styles.distributedRow}>
          {requiredFields.firstName && (
            <TextInput
              aria-label="First Name"
              autoComplete="given-name"
              // The max length matches the server-imposed limit.
              maxLength={250}
              name="firstName"
              onChange={handleInputChange}
              placeholder="First Name"
              required
              value={formState.firstName}
            />
          )}
          {requiredFields.lastName && (
            <TextInput
              aria-label="Last Name"
              autoComplete="family-name"
              // The max length matches the server-imposed limit.
              maxLength={250}
              name="lastName"
              onChange={handleInputChange}
              placeholder="Last Name"
              required
              value={formState.lastName}
            />
          )}
        </div>
      )}

      {requiredFields.email && (
        <TextInput
          aria-label="Email"
          autoComplete="email"
          // The max length matches the server-imposed limit.
          maxLength={250}
          name="email"
          onChange={handleInputChange}
          placeholder="Email"
          required
          type="email"
          value={formState.email}
        />
      )}

      <Button
        type="submit"
        loading={isLoading}
        variant="primary"
        disabled={isLoading}
      >
        <FormattedMessage
          defaultMessage="Enter Giveaway"
          id="giveaways.entryForm.enterButton"
        />
      </Button>
      <div className={styles.footer}>
        <p className="body-md text-color-tertiary">
          <FormattedMessage
            defaultMessage="By entering your email, you consent to receive giveaway updates and marketing communications."
            id="giveaways.entryForm.disclaimer"
          />
        </p>
      </div>
    </form>
  )
}
